import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';

const calendarClassNames = {
  months: "flex flex-col sm:flex-row gap-4",
  month: "flex-1",
  caption: "flex justify-center pt-1 relative items-center mb-2",
  caption_label: "font-heading text-base text-healing-burgundy",
  nav_button: "h-8 w-8 bg-transparent p-0 text-healing-burgundy-light hover:text-healing-purple",
  head_cell: "text-healing-burgundy-light font-body text-xs w-10",
  cell: "h-10 w-10 text-center p-0 relative",
  day: "h-10 w-10 p-0 font-body text-sm hover:bg-healing-purple/10 rounded-full transition-colors",
  day_selected: "bg-healing-purple text-white hover:bg-healing-purple-hover rounded-full",
  day_today: "font-bold text-healing-purple",
  day_disabled: "text-gray-300",
};

export function BookingCalendar({ 
  selectedDate, 
  selectedTime, 
  onDateSelect, 
  onTimeSelect, 
  isDayAvailable, 
  isDayFullyBooked, 
  isDayBlocked,
  getTimeSlotsForDay,
  availableTimeSlots 
}) {
  const calendarModifiers = useMemo(() => ({
    booked: (date) => isDayFullyBooked(date),
    blocked: (date) => isDayBlocked(date)
  }), [isDayFullyBooked, isDayBlocked]);

  const calendarModifiersStyles = useMemo(() => ({
    booked: { textDecoration: 'line-through', color: '#ccc' },
    blocked: { textDecoration: 'line-through', color: '#fca5a5' }
  }), []);

  const allSlots = useMemo(() => 
    selectedDate ? getTimeSlotsForDay(selectedDate) : [],
    [selectedDate, getTimeSlotsForDay]
  );

  return (
    <>
      {/* Calendar */}
      <div className="md:col-span-2 space-y-3">
        <Label className="text-healing-burgundy">Select a Date *</Label>
        <div className="border border-healing-border rounded-2xl p-4 bg-healing-cream-alt" data-testid="booking-calendar-inline">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={onDateSelect}
            numberOfMonths={typeof window !== 'undefined' && window.innerWidth >= 768 ? 2 : 1}
            disabled={(date) => {
              if (date < new Date(new Date().setHours(0,0,0,0))) return true;
              if (!isDayAvailable(date)) return true;
              if (isDayFullyBooked(date)) return true;
              return false;
            }}
            modifiers={calendarModifiers}
            modifiersStyles={calendarModifiersStyles}
            className="rounded-xl"
            classNames={calendarClassNames}
          />
          {selectedDate && (
            <p className="mt-3 text-sm text-healing-purple font-medium text-center">
              Selected: {format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </p>
          )}
        </div>
      </div>

      {/* Time pills */}
      {selectedDate && (
        <div className="md:col-span-2 space-y-3">
          <Label className="text-healing-burgundy">Select a Time *</Label>
          {availableTimeSlots.length > 0 ? (
            <div className="flex flex-wrap gap-2" data-testid="booking-time-pills">
              {allSlots.map(time => {
                const available = availableTimeSlots.includes(time);
                const selected = selectedTime === time;
                return (
                  <button
                    key={time}
                    type="button"
                    disabled={!available}
                    onClick={() => onTimeSelect(time)}
                    className={`px-4 py-2 rounded-full text-sm font-body border transition-all ${
                      selected 
                        ? 'bg-healing-purple text-white border-healing-purple shadow-md scale-105' 
                        : available 
                          ? 'bg-white border-healing-border text-healing-burgundy hover:border-healing-purple hover:text-healing-purple' 
                          : 'bg-gray-100 border-gray-200 text-gray-300 line-through cursor-not-allowed'
                    }`}
                    data-testid={`time-pill-${time.replace(/[\s:]/g, '-')}`}
                  >
                    {time}
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-red-500 font-body">No available slots for this date. Please try another day.</p>
          )}
        </div>
      )}
    </>
  );
}
